import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Draft {
  id: string;
  user_id: string;
  topic_id: string;
  content: string;
  word_count: number;
  is_submitted: boolean;
  created_at: string;
  updated_at: string;
  topics: {
    topic: string;
    task_type: number;
  };
  evaluations?: {
    overall_score: number;
  }[];
}

export function useDrafts(options?: { 
  isSubmitted?: boolean;
  limit?: number;
  orderBy?: 'created_at' | 'updated_at';
  orderDirection?: 'asc' | 'desc';
  taskType?: number;  
}) {
  const { user } = useAuth();

  return useQuery<Draft[]>({
    queryKey: ['drafts', user?.id, options],
    queryFn: async () => {
      if (!user) return [];

      let query = supabase
      .from("drafts")
      .select(`
        *,
        topics!inner (
          topic,
          task_type
        ),
        evaluations (
          overall_score
        )
      `)
      .eq("user_id", user.id);
    
      if (typeof options?.isSubmitted === 'boolean') {
        query = query.eq("is_submitted", options.isSubmitted);
      }

      if (options?.taskType) {
        query = query.eq("topics.task_type", options.taskType);
      }

      if (options?.orderBy) {
        query = query.order(options.orderBy, { 
          ascending: options.orderDirection === 'asc' 
        });
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as Draft[];
    },
    enabled: !!user,
  });
}